import type { EnumType } from '../enums/EnumType';
import type { ISpecDatatypes } from '../spec/SpecDatatypes';
import type { FIXValue } from '../util/util';
import type { CategoryType } from './categories/CategoryType';
import type { SectionType } from './sections/SectionType';

/**
 * Field is a predefined data element, identified by a unique tag number,
 * that represents a specific piece of information within a message
 * (such as price, quantity, or order ID).
 *
 * @public
 */
export class Field {
    /** The unique tag number identifying this field */
    public tag: number;
    /** The value of the field */
    public value: FIXValue;
    /** The name of the field */
    public name: string | undefined = undefined;
    /** A description of what the field represents */
    public description: string | undefined = undefined;
    /** The data type of the field */
    public type: ISpecDatatypes | undefined = undefined;
    /** The category this field belongs to */
    public category: CategoryType | undefined = undefined;
    /** The section this field belongs to */
    public section: SectionType | undefined = undefined;
    /** The enumeration type if this field is an enumerated value */
    public enumeration: EnumType | undefined = undefined;

    /**
     * Creates a new Field instance with the specified tag and value.
     *
     * @param {number} tag - The unique tag number identifying this field
     * @param {FIXValue} value - The value to be stored in this field
     */
    constructor(tag: number, value: FIXValue) {
        this.tag = tag >> 0;
        this.value = value;
        this.name = undefined;
        this.description = undefined;
        this.type = undefined;
        this.category = undefined;
        this.section = undefined;
        this.enumeration = undefined;
    }

    /**
     * Sets the tag number for this field.
     *
     * @param {number} tag - The new tag number
     */
    public setTag(tag: number): void {
        this.tag = tag >> 0;
    }

    /**
     * Sets the value for this field.
     *
     * @param {FIXValue} value - The new value
     */
    public setValue(value: FIXValue): void {
        this.value = value;
    }

    /**
     * Sets the name for this field.
     *
     * @param {string} name - The new name
     */
    public setName(name: string): void {
        this.name = name;
    }

    /**
     * Sets the description for this field.
     *
     * @param {string} description - The new description
     */
    public setDescription(description: string): void {
        this.description = description;
    }

    /**
     * Sets the data type for this field.
     *
     * @param {ISpecDatatypes | undefined} type - The new data type
     */
    public setType(type: ISpecDatatypes | undefined): void {
        this.type = type;
    }

    /**
     * Sets the category for this field.
     *
     * @param {CategoryType} category - The new category
     */
    public setCategory(category: CategoryType): void {
        this.category = category;
    }

    /**
     * Sets the section for this field.
     *
     * @param {SectionType} section - The new section
     */
    public setSection(section: SectionType): void {
        this.section = section;
    }

    /**
     * Sets the enumeration type for this field.
     *
     * @param {EnumType} enumeration - The new enumeration type
     */
    public setEnumeration(enumeration: EnumType): void {
        this.enumeration = enumeration;
    }

    /**
     * Returns a string representation of the field in FIX format (tag=value).
     *
     * @returns {string} The string representation of the field
     */
    public toString(): string {
        return `${this.tag}=${this.value}`;
    }
}
